import { useQuery, useMutation, useQueryClient } from 'react-query'
import { workOrderService } from '@services/workOrderService'
import { CreateWorkOrderRequest, UpdateWorkOrderRequest } from '../types/workOrder'
import { useAuthStore } from '@store/useAuthStore'
import { invalidateNotifications } from '@utils/notificationQueries'

export function useWorkOrders(params?: {
  workshopCompanyId?: number
  customerCompanyId?: number
  vehicleId?: number
  status?: string
  page?: number
  size?: number
}) {
  return useQuery(
    ['workOrders', params],
    () => {
      console.log('useWorkOrders - Fetching with params:', params)
      return workOrderService.getAll(params)
    },
    {
      enabled: !!params?.workshopCompanyId,
      staleTime: 0, // Always refetch
      cacheTime: 0, // Don't cache
      onSuccess: (data) => {
        console.log('useWorkOrders - Success, received work orders:', data?.length || 0)
      },
      onError: (error) => {
        console.error('useWorkOrders - Error:', error)
      }
    }
  )
}

export function useWorkOrder(id: number, mechanicId?: number) {
  return useQuery(['workOrder', id, mechanicId], () => workOrderService.getById(id, mechanicId), {
    enabled: !!id,
  })
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient()
  const userId = useAuthStore.getState().user?.id

  return useMutation(
    (data: CreateWorkOrderRequest) => workOrderService.create(data),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries('workOrders')
        invalidateNotifications(queryClient, userId)
        await queryClient.refetchQueries(['workOrders'])
      },
    }
  )
}

export function useUpdateWorkOrder() {
  const queryClient = useQueryClient()
  const userId = useAuthStore.getState().user?.id
  
  return useMutation(
    ({ id, data }: { id: number; data: UpdateWorkOrderRequest }) =>
      workOrderService.update(id, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries('workOrders')
        queryClient.invalidateQueries(['workOrder', variables.id])
        if (variables.data.assignedMechanicId !== undefined) {
          invalidateNotifications(queryClient, userId)
        }
      },
    }
  )
}

export function useApproveWorkOrder() {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, approvedBy }: { id: number; approvedBy: number }) =>
      workOrderService.approve(id, approvedBy),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries('workOrders')
        queryClient.invalidateQueries(['workOrder', variables.id])
      },
    }
  )
}

export function useCancelWorkOrder() {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, reason }: { id: number; reason?: string }) =>
      workOrderService.cancel(id, reason),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries('workOrders')
        queryClient.invalidateQueries(['workOrder', variables.id])
      },
    }
  )
}


